import { useCallback, useEffect, useState } from 'react';
import type { Translations } from './interfaces/translationTypes';
import { useLocalStorage } from '@/app/hooks/useLocalStorage';
import { analytics } from '@/shared/services/analytics.service';

import { useTranslation } from 'react-i18next';
import type { TOptions } from 'i18next';

export const useTranslations = <N extends keyof Translations>(
  namespace?: N,
) => {
  const { t, i18n } = useTranslation(namespace);
  const { setLocalStorage, getLocalStorage } = useLocalStorage();

  const [activeLanguage, setActiveLanguage] = useState(() => {
    return i18n.language;
  });

  const applyLanguage = async (lang: string) => {
    await i18n.changeLanguage(lang);
    setLocalStorage({
      key: 'language',
      value: lang,
    });
    setActiveLanguage(lang);
  };

  /** User-initiated change (settings, login dropdown); the init effect uses `applyLanguage`. */
  const setLanguage = async (lang: string) => {
    await applyLanguage(lang);
    analytics.capture('settings:language_changed', {
      language: lang,
      source: window.location.pathname.startsWith('/auth') ? 'login' : 'app',
    });
  };

  useEffect(() => {
    const initializeLanguage = async () => {
      let lang: string | null = getLocalStorage({ key: 'language' });

      if (!lang) {
        const browserLang = navigator.language;
        lang = browserLang.startsWith('es')
          ? 'es'
          : browserLang.startsWith('pt')
          ? 'pt'
          : browserLang.startsWith('en')
          ? 'en'
          : 'es';
      }

      // Solo cambiar si es diferente al actual
      if (lang !== i18n.language) {
        await applyLanguage(lang);
      }
    };

    initializeLanguage();
  }, []);

  // Memoized so `translation` is a stable reference across renders. Without
  // this, any useCallback that lists it as a dependency re-creates on every
  // render, and a useEffect driven by that callback re-fires forever — the
  // Meta Ads settings page looped its status+accounts requests exactly this
  // way. i18next's `t` changes identity on language switches, which is the one
  // time consumers genuinely need to re-run.
  const translation = useCallback(
    <K extends keyof Translations[N]>(
      key: K,
      options?: TOptions,
    ): Translations[N][K] => {
      return t(key as string, options) as Translations[N][K];
    },
    [t],
  );

  return {
    translation,
    setLanguage,
    activeLanguage,
  };
};

export default useTranslations;
